/**
 * VittoriaBanner — banner celebrativo del vincitore della stagione conclusa.
 *
 * - Se l'utente gestisce (presidente o allenatore secondo) la squadra
 *   campione, mostra banner full-width "Hai vinto..." con coriandoli al
 *   primo accesso post-vittoria (localStorage flag per non rifare l'anim).
 * - Altrimenti mostra una card sobria con il riepilogo dei vincitori.
 *
 * Sparisce automaticamente quando non c'e' nessuna stagione conclusa.
 */

import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { useAuth } from "../../hooks/useAuth";
import { useSquadre, useMieSquadreGestite } from "../../hooks/useFase7";
import { usePalmaresUltimaConclusa } from "../../hooks/useStagione";
import { tr } from "../../i18n";

type Trofeo = "campionato" | "cl" | "supercoppa";

const TROFEO_EMOJI: Record<Trofeo, string> = {
  campionato: "🏆",
  cl: "⭐",
  supercoppa: "👑",
};

const TROFEO_LABEL: Record<Trofeo, string> = {
  campionato: tr("Campionato"),
  cl: tr("Coppa"),
  supercoppa: tr("Supercoppa"),
};

function lanciaCoriandoli() {
  // Burst centrale + 2 laterali continui per ~3s. Palette oro/viola/verde
  // coerente con la palette ruoli della Lega.
  const colors = ["#FBBF24", "#F59E0B", "#A855F7", "#22C55E", "#FFFFFF"];
  confetti({
    particleCount: 120,
    spread: 90,
    origin: { y: 0.6 },
    colors,
  });
  const duration = 2500;
  const end = Date.now() + duration;
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export function VittoriaBanner() {
  const { user } = useAuth();
  const { data: palmares } = usePalmaresUltimaConclusa();
  const { data: squadre } = useSquadre();
  const { data: idsGestite } = useMieSquadreGestite();
  const launchedRef = useRef(false);

  // Calcolo "ho vinto" — null fino a quando i dati arrivano
  const userSquadreIds = (() => {
    if (!user || !squadre) return null;
    const set = new Set<string>();
    for (const s of squadre) {
      if (s.presidente_user_id === user.id) set.add(s.id);
    }
    for (const id of idsGestite ?? []) set.add(id);
    return set;
  })();

  const trofeiVinti: Trofeo[] = (() => {
    if (!palmares || !userSquadreIds) return [];
    const out: Trofeo[] = [];
    if (palmares.campionato_squadra_id && userSquadreIds.has(palmares.campionato_squadra_id))
      out.push("campionato");
    if (palmares.cl_squadra_id && userSquadreIds.has(palmares.cl_squadra_id)) out.push("cl");
    if (palmares.supercoppa_squadra_id && userSquadreIds.has(palmares.supercoppa_squadra_id))
      out.push("supercoppa");
    return out;
  })();

  const hoVinto = trofeiVinti.length > 0;
  const stagioneId = palmares?.stagione_id ?? null;

  // Coriandoli una sola volta per stagione (localStorage flag).
  useEffect(() => {
    if (!hoVinto || !stagioneId) return;
    if (launchedRef.current) return;
    const key = `seenVittoria-${stagioneId}`;
    if (localStorage.getItem(key)) return;
    launchedRef.current = true;
    // Piccolo delay per dare tempo al banner di renderizzarsi
    const t = setTimeout(() => {
      lanciaCoriandoli();
      localStorage.setItem(key, "1");
    }, 350);
    return () => clearTimeout(t);
  }, [hoVinto, stagioneId]);

  if (!palmares) return null;
  // Niente da mostrare se la stagione conclusa non ha alcun vincitore
  // (es. campionato non ancora calcolato → posizione=1 vuota)
  if (!palmares.campionato_squadra_id && !palmares.cl_squadra_id && !palmares.supercoppa_squadra_id)
    return null;

  const nomeSquadra = (id: string | null) =>
    id ? (squadre?.find((s) => s.id === id)?.nome ?? "—") : "—";

  if (hoVinto) {
    const titolo =
      trofeiVinti.length === 3
        ? tr("🎉 TRIPLETE! Hai vinto tutto")
        : trofeiVinti.length === 2
          ? tr("🎉 Doppietta — Hai vinto {{n}} trofei", { n: trofeiVinti.length })
          : trofeiVinti[0] === "campionato"
            ? tr("🏆 Hai vinto il campionato {{anno}}!", { anno: palmares.anno })
            : trofeiVinti[0] === "cl"
              ? tr("⭐ Hai vinto la {{coppa}} {{anno}}!", {
                  coppa: palmares.cl_nome ?? tr("coppa"),
                  anno: palmares.anno,
                })
              : tr("👑 Hai vinto la Supercoppa {{anno}}!", { anno: palmares.anno });

    return (
      <div className="rounded-card border-2 border-yellow-400/70 bg-gradient-to-r from-yellow-500/25 via-amber-300/20 to-yellow-500/25 text-center py-6 px-4 space-y-3 shadow-lg shadow-yellow-500/20">
        <h2 className="font-display text-2xl md:text-3xl text-yellow-200 tracking-wide drop-shadow">
          {titolo}
        </h2>
        <div className="text-sm text-fg">
          {tr("Stagione")} <strong>{palmares.anno}</strong> {tr("· sei nella storia della Lega.")}
        </div>
        {trofeiVinti.length > 1 && (
          <div className="flex justify-center gap-4 mt-1 text-3xl">
            {trofeiVinti.map((t) => (
              <span key={t} title={TROFEO_LABEL[t]}>
                {TROFEO_EMOJI[t]}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Vista per chi non ha vinto: riepilogo sobrio dei vincitori
  return (
    <div className="rounded-card border border-accent/30 bg-primary-deep/40 p-4">
      <div className="font-mono text-[10px] text-fg-muted uppercase tracking-[0.15em] mb-2">
        🏁 Stagione {palmares.anno} conclusa
      </div>
      <div className="space-y-1 text-sm">
        {palmares.campionato_squadra_id && (
          <div>
            🏆 <strong className="text-fg">{nomeSquadra(palmares.campionato_squadra_id)}</strong>{" "}
            {tr("— Campione")}
          </div>
        )}
        {palmares.cl_squadra_id && (
          <div>
            ⭐ <strong className="text-fg">{nomeSquadra(palmares.cl_squadra_id)}</strong> —
            {palmares.cl_nome ?? tr("Coppa")}
          </div>
        )}
        {palmares.supercoppa_squadra_id && (
          <div>
            👑 <strong className="text-fg">{nomeSquadra(palmares.supercoppa_squadra_id)}</strong>{" "}
            {tr("— Supercoppa")}
          </div>
        )}
      </div>
    </div>
  );
}
